<template>
  <div>
    <Breadcrumbs main="Admin" title="Companies" />
    <div class="container-fluid">
      <div class="row">
        <div class="col-12">
          <div class="card">
            <div class="card-header d-flex justify-content-between align-items-center">
              <div>
                <h5 class="mb-0">Law Firms</h5>
                <span class="text-muted">Manage tenant companies and provisioning</span>
              </div>
              <button class="btn btn-primary" type="button" @click="openCreate">
                <vue-feather type="plus" class="me-1" />
                Add Company
              </button>
            </div>
            <div class="card-body">
              <div v-if="error" class="alert alert-danger" role="alert">
                {{ error }}
              </div>
              <div v-if="loading" class="text-center py-4 text-muted">
                Loading companies...
              </div>
              <div v-else-if="companies.length === 0" class="text-center py-4 text-muted">
                No companies registered yet.
              </div>
              <div v-else class="table-responsive">
                <table class="table table-hover">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Company</th>
                      <th>Code</th>
                      <th>Email</th>
                      <th>Location</th>
                      <th>Admin Contact</th>
                      <th>Status</th>
                      <th>Created</th>
                      <th class="text-end">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="company in companies" :key="company.companyId">
                      <td>{{ company.companyId }}</td>
                      <td>
                        <strong>{{ company.companyName }}</strong>
                        <div v-if="company.databaseName" class="text-muted f-12">
                          {{ company.databaseName }}
                        </div>
                      </td>
                      <td><code>{{ company.companyCode || '—' }}</code></td>
                      <td>{{ company.email }}</td>
                      <td>{{ company.location || '—' }}</td>
                      <td>
                        <span v-if="company.adminName">{{ company.adminName }}</span>
                        <span v-else class="text-muted">—</span>
                        <div v-if="company.adminEmail" class="text-muted f-12">{{ company.adminEmail }}</div>
                      </td>
                      <td>
                        <span
                          class="badge"
                          :class="company.active ? 'badge-success' : 'badge-secondary'"
                        >
                          {{ company.active ? 'Active' : 'Inactive' }}
                        </span>
                      </td>
                      <td>{{ formatDate(company.createdAt) }}</td>
                      <td class="text-end">
                        <button
                          class="btn btn-sm btn-outline-primary me-1"
                          type="button"
                          title="Edit"
                          @click="openEdit(company)"
                        >
                          <vue-feather type="edit-2" />
                        </button>
                        <button
                          class="btn btn-sm btn-outline-danger"
                          type="button"
                          title="Delete"
                          @click="confirmDelete(company)"
                        >
                          <vue-feather type="trash-2" />
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <teleport to="body">
      <div
        v-if="showFormModal"
        class="modal fade show d-block"
        tabindex="-1"
        role="dialog"
        aria-modal="true"
      >
        <div class="modal-dialog modal-lg modal-dialog-centered" role="document">
          <div class="modal-content">
            <form @submit.prevent="submitForm">
              <div class="modal-header">
                <h5 class="modal-title">{{ isEditing ? 'Edit Company' : 'Add Company' }}</h5>
                <button type="button" class="btn-close" aria-label="Close" @click="closeFormModal" />
              </div>
              <div class="modal-body">
                <div v-if="formError" class="alert alert-danger" role="alert">
                  {{ formError }}
                </div>
                <div class="row">
                  <div class="col-md-6">
                    <div class="form-group form-field mb-2">
                      <label class="form-label">Company name <span class="text-danger">*</span></label>
                      <input v-model="form.companyName" class="form-control" type="text" required>
                    </div>
                  </div>
                  <div class="col-md-6">
                    <div class="form-group form-field mb-2">
                      <label class="form-label">Company code <span class="text-danger">*</span></label>
                      <input
                        v-model="form.companyCode"
                        class="form-control"
                        type="text"
                        :required="!isEditing"
                        :readonly="isEditing"
                        :disabled="isEditing"
                        placeholder="e.g. acme-law"
                        pattern="[a-z0-9]+(?:-[a-z0-9]+)*"
                        title="Lowercase letters, numbers, and hyphens only"
                      >
                      <p class="text-muted f-12 mb-0 mt-1">
                        {{ isEditing
                          ? 'Company code cannot be changed after creation.'
                          : 'Unique identifier used by firm users to sign in.' }}
                      </p>
                    </div>
                  </div>
                  <div class="col-md-6">
                    <div class="form-group form-field mb-2">
                      <label class="form-label">Company email <span class="text-danger">*</span></label>
                      <input v-model="form.email" class="form-control" type="email" required>
                    </div>
                  </div>
                  <div class="col-md-6">
                    <div class="form-group form-field mb-2">
                      <label class="form-label">Phone</label>
                      <input v-model="form.phoneNo" class="form-control" type="text">
                    </div>
                  </div>
                  <div class="col-md-6">
                    <div class="form-group form-field mb-2">
                      <label class="form-label">Location</label>
                      <input v-model="form.location" class="form-control" type="text">
                    </div>
                  </div>
                  <div class="col-12">
                    <div class="form-group form-field mb-2">
                      <label class="form-label">Address</label>
                      <input v-model="form.address" class="form-control" type="text">
                    </div>
                  </div>
                  <div class="col-md-4">
                    <div class="form-group form-field mb-2">
                      <label class="form-label">Admin name</label>
                      <input v-model="form.adminName" class="form-control" type="text">
                    </div>
                  </div>
                  <div class="col-md-4">
                    <div class="form-group form-field mb-2">
                      <label class="form-label">Admin email</label>
                      <input v-model="form.adminEmail" class="form-control" type="email">
                    </div>
                  </div>
                  <div class="col-md-4">
                    <div class="form-group form-field mb-2">
                      <label class="form-label">Admin phone</label>
                      <input v-model="form.adminPhoneNo" class="form-control" type="text">
                    </div>
                  </div>
                  <div v-if="isEditing" class="col-12">
                    <div class="form-check form-switch">
                      <input
                        id="company-active"
                        v-model="form.active"
                        class="form-check-input"
                        type="checkbox"
                      >
                      <label class="form-check-label" for="company-active">Active</label>
                    </div>
                    <p class="text-muted f-12 mb-0">
                      Inactive companies cannot sign in via tenant auth.
                    </p>
                  </div>
                </div>
              </div>
              <div class="modal-footer">
                <button type="button" class="btn btn-secondary" @click="closeFormModal">
                  Cancel
                </button>
                <button type="submit" class="btn btn-primary" :disabled="saving">
                  {{ saving ? 'Saving...' : (isEditing ? 'Save Changes' : 'Create Company') }}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
      <div v-if="showFormModal" class="modal-backdrop fade show" @click="closeFormModal" />
    </teleport>

    <teleport to="body">
      <div
        v-if="companyToDelete"
        class="modal fade show d-block"
        tabindex="-1"
        role="dialog"
        aria-modal="true"
      >
        <div class="modal-dialog modal-dialog-centered" role="document">
          <div class="modal-content">
            <div class="modal-header">
              <h5 class="modal-title">Delete Company</h5>
              <button type="button" class="btn-close" aria-label="Close" @click="companyToDelete = null" />
            </div>
            <div class="modal-body">
              <p class="mb-2">
                Soft-delete <strong>{{ companyToDelete.companyName }}</strong>?
              </p>
              <p class="text-muted mb-0 f-12">
                The company will be deactivated and hidden from this list. The tenant database is retained.
              </p>
              <div v-if="deleteError" class="alert alert-danger mt-3 mb-0" role="alert">
                {{ deleteError }}
              </div>
            </div>
            <div class="modal-footer">
              <button type="button" class="btn btn-secondary" @click="companyToDelete = null">
                Cancel
              </button>
              <button type="button" class="btn btn-danger" :disabled="deleting" @click="submitDelete">
                {{ deleting ? 'Deleting...' : 'Delete' }}
              </button>
            </div>
          </div>
        </div>
      </div>
      <div v-if="companyToDelete" class="modal-backdrop fade show" @click="companyToDelete = null" />
    </teleport>
  </div>
</template>

<script>
import {
  listCompanies,
  createCompany,
  updateCompany,
  deleteCompany,
} from '@/services/companyService'

const emptyForm = () => ({
  companyName: '',
  companyCode: '',
  email: '',
  address: '',
  location: '',
  phoneNo: '',
  adminName: '',
  adminPhoneNo: '',
  adminEmail: '',
  active: true,
})

export default {
  data() {
    return {
      companies: [],
      loading: false,
      error: '',
      showFormModal: false,
      isEditing: false,
      editingId: null,
      form: emptyForm(),
      formError: '',
      saving: false,
      companyToDelete: null,
      deleteError: '',
      deleting: false,
    }
  },

  created() {
    this.loadCompanies()
  },

  methods: {
    async loadCompanies() {
      this.loading = true
      this.error = ''
      try {
        this.companies = await listCompanies()
      } catch (err) {
        this.error = err.message || 'Failed to load companies'
      } finally {
        this.loading = false
      }
    },

    formatDate(value) {
      if (!value) return '—'
      return new Date(value).toLocaleDateString()
    },

    openCreate() {
      this.isEditing = false
      this.editingId = null
      this.form = emptyForm()
      this.formError = ''
      this.showFormModal = true
    },

    openEdit(company) {
      this.isEditing = true
      this.editingId = company.companyId
      this.form = {
        companyName: company.companyName || '',
        companyCode: company.companyCode || '',
        email: company.email || '',
        address: company.address || '',
        location: company.location || '',
        phoneNo: company.phoneNo || '',
        adminName: company.adminName || '',
        adminPhoneNo: company.adminPhoneNo || '',
        adminEmail: company.adminEmail || '',
        active: company.active,
      }
      this.formError = ''
      this.showFormModal = true
    },

    closeFormModal() {
      if (this.saving) return
      this.showFormModal = false
      this.formError = ''
    },

    buildPayload() {
      const payload = {
        companyName: this.form.companyName.trim(),
        email: this.form.email.trim(),
      }

      const optionalFields = [
        'address',
        'location',
        'phoneNo',
        'adminName',
        'adminPhoneNo',
        'adminEmail',
      ]

      optionalFields.forEach((field) => {
        const value = this.form[field]?.trim()
        if (value) payload[field] = value
      })

      if (this.isEditing) {
        payload.companyId = this.editingId
        payload.active = this.form.active
      } else {
        payload.companyCode = this.form.companyCode.trim()
      }

      return payload
    },

    async submitForm() {
      this.formError = ''
      this.saving = true

      try {
        const payload = this.buildPayload()
        if (this.isEditing) {
          await updateCompany(payload)
        } else {
          await createCompany(payload)
        }
        this.showFormModal = false
        await this.loadCompanies()
      } catch (err) {
        this.formError = err.message || 'Request failed'
      } finally {
        this.saving = false
      }
    },

    confirmDelete(company) {
      this.companyToDelete = company
      this.deleteError = ''
    },

    async submitDelete() {
      this.deleteError = ''
      this.deleting = true

      try {
        await deleteCompany(this.companyToDelete.companyId)
        this.companyToDelete = null
        await this.loadCompanies()
      } catch (err) {
        this.deleteError = err.message || 'Failed to delete company'
      } finally {
        this.deleting = false
      }
    },
  },
}
</script>
